import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { focusButton, highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { InventoryLocationsService } from 'src/app/shared/services/inventory-locations.service';
import { PinService } from 'src/app/shared/services/pin.service';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { GetInventoryOnLocation, MoveInventory, ValidateTransactionDateForInventories } from 'src/app/shared/states/inventory/inventory.actions';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';

@Component({
  selector: 'app-move-inventory-dialog',
  templateUrl: './move-inventory-dialog.component.html',
  styleUrls: ['./move-inventory-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [InventoryLocationsService, BackCountService]
})
@UntilDestroy()
export class MoveInventoryDialogComponent implements OnInit {

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() successfullySaved: EventEmitter<void> = new EventEmitter();
  @Output() failed: EventEmitter<void> = new EventEmitter();

  form: FormGroup;

  private drugID: '';
  private pinChannel = 'moveInventory';
  
  @ViewChild('moveQty') moveQty;
  @ViewChild('submitButton') submitButton;
  @ViewChild('cancelButton') cancelButton;
  @ViewChild('from') from;
  @ViewChild('transactionDatePicker') transactionDatePicker;

  searchSingleLocation: boolean = this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled);

  @Select(InventoryState.success) success$: Observable<boolean>;

  backCountEnabledForMoveFrom: boolean;
  backCountEnabledForMoveTo: boolean;
  /**
   *
   */
  constructor(private fb: FormBuilder, private store: Store, private cd: ChangeDetectorRef, private pinService: PinService,
    private formattedDatePipe: FormattedDatePipe, private transactionsService: TransactionsService, private inventoryLocationsService: InventoryLocationsService,
    private backCountService: BackCountService ) {
    this.form = fb.group({
      drug: ['', Validators.required],
      locationFromId: ['', Validators.required],
      locationFromOnHand: 0,
      locationToId: ['', Validators.required],
      locationToOnHand: 0,
      note: '',
      moveQty: '',
      transactionDateDisplayValue: formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy'),
      transactionDate: new Date(),
      moveFromBackCountMatches: null,
      moveToBackCountMatches: null
    });
    
  }

  ngOnInit(): void {
    this.form.get('locationFromId')
    .valueChanges
    .pipe(untilDestroyed(this), distinctUntilChanged())
    .subscribe(val => {
      if (!val) return;
      this.store.dispatch(new GetInventoryOnLocation(val, this.drugID))
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        const currentInventoryOnLocation = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
        this.form.get('locationFromOnHand').setValue(currentInventoryOnLocation.onHand);
        this.form.get('moveQty').setValidators([Validators.required, Validators.max(currentInventoryOnLocation.onHand), Validators.min(0)]);
        this.form.get('moveQty').updateValueAndValidity();
        this.cd.detectChanges();
      });
    });

    this.form.get('locationToId')
    .valueChanges
    .pipe(untilDestroyed(this), distinctUntilChanged())
    .subscribe(val => {
      if (!val) return;
      this.store.dispatch(new GetInventoryOnLocation(val, this.drugID))
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        const currentInventoryOnLocation = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
        this.form.get('locationToOnHand').setValue(currentInventoryOnLocation.onHand);
        this.cd.detectChanges();
      });
    });

    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(res => {
      if (res?.success && res.dialogId === this.pinChannel) {
         this.moveInventoryRequest(res.pin);
         this.pinService.closeDialog();
      }
    });
  }

  save(): void {
    if (this.form.invalid) {
      highlightRequiredControlsWithErrors(this.form);
    } else {
      this.validate();
    }
  }

  ndcSelected(ndc): void {
    this.drugID = ndc?.drugID;

    this.inventoryLocationsService.addLocationsWithPositiveInventory(this.drugID, ndc?.positiveInventoryBalanceLocationIDs);

    this.backCountEnabledForMoveFrom = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.MovedFrom, ndc.schedule);
    if (this.backCountEnabledForMoveFrom) {
     this.form.get('moveFromBackCountMatches').setValidators(Validators.required);
    }
    this.backCountEnabledForMoveTo = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.MovedTo, ndc.schedule);
    if(this.backCountEnabledForMoveTo) {
     this.form.get('moveToBackCountMatches').setValidators(Validators.required);
    }

    this.form.get('drug').setValue(ndc || null);
    this.form.get('locationFromId').setValue(ndc?.locationID || null);
    this.form.get('locationFromOnHand').setValue(ndc?.onHand || 0);

    this.form.updateValueAndValidity();
  }

  saveAddedNote(note: string): void {
    this.form.get('note').setValue(note);
  }

  removeNote(): void {
    this.form.get('note').setValue(null);
  }

  deleteNDC(): void {
    this.form.reset();
  }

  getMoveFromEndingQty(): number {
    const formValue = this.form.getRawValue();
    return +formValue.locationFromOnHand - +formValue.moveQty;
  }

  getMoveToEndingQty(): number {
    const formValue = this.form.getRawValue();
    return +formValue.locationToOnHand+ +formValue.moveQty;
  }

  focusControl(): void {
    if (this.form.invalid) {
      focusButton(this.cancelButton);
    } else {
      focusButton(this.submitButton);
    }
  }

  focusDatePicker(): void {
    setTimeout(() => {
      this.transactionDatePicker.focusInput();
    });
  }

  validate(): void { 
    if (this.form.valid) { 
      const formValue = this.form.getRawValue();
      const payload = {
        transactionDate: formValue?.transactionDate,
        inventoryActions: [ 
          {
            drugId: this.drugID,
            addedQuantity: formValue.moveQty,
            activityType: ActivityTypesEnum.MovedFrom,
            locationId : formValue.locationFromId
          },
        ]
      }
      this.store.dispatch(new ValidateTransactionDateForInventories(payload))
      .pipe(untilDestroyed(this))
        .subscribe(resp => {
          const response = resp?.inventory?.response;
          if(response?.success && !response?.value?.length) {
            this.pinService.openPinDialog(this.pinChannel);
          } else if (response?.success) {
            this.transactionsService.handleTransactionDateErrors(response.value);
          }
        });
    }
  }

  private moveInventoryRequest(pin: number): void {
    const formValue = this.form.getRawValue();
    const payload = {
      pin,
      drugId: this.drugID,
      locationFromId: formValue.locationFromId,
      locationToId: formValue.locationToId,
      moveQty: formValue.moveQty,
      note: formValue.note,
      transactionDate: formValue.transactionDate,
      moveFromBackCountMatches: formValue?.moveFromBackCountMatches,
      moveToBackCountMatches: formValue?.moveToBackCountMatches
    };
    this.store.dispatch(new MoveInventory(payload))
    .pipe(untilDestroyed(this), withLatestFrom(this.success$))
    .subscribe(([_, success]) => {
      if (success) {
        this.successfullySaved.emit();
      }
      else this.failed.emit();
    })
  }

  dateTimeChanged(date): void {
    this.form.get('transactionDate')?.setValue(date?.value);
  }

  now(): Date {
    return new Date();
  }

  focusFrom(): void {
    this.from?.focus();
  }
  
}


